// Company revenue — what the business earned, across every advertiser.
//
// The fleet-grain sibling of the device ledger, and it obeys the same law:
// NOTHING IS COMPUTED HERE. Every number arrives from signals.company_day_revenue
// (V315) via /api/insights/revenue. Rates in particular are recomputed server-side
// from summed numerators and denominators — averaging the daily CPMs would weight
// a 40-play day like a 30,000-play one and publish a figure nobody could reproduce
// from the rows below it.
//
// THE ONE EDITORIAL DECISION ON THIS SCREEN, and it is deliberate.
//
// 88% of accrued revenue in the 30 days to 2026-08-30 sat under PAUSED campaigns
// owned by the fleet operator, not under a paying advertiser. A headline that
// folds those into one number is arithmetically right and commercially a lie. So
// the hero is the ACTIVE-advertiser figure, with the paused block shown beside it
// rather than hidden — filtering it away would be its own kind of dishonesty, and
// the total has to stay reachable or this screen stops reconciling with billing.

import React, { useMemo, useCallback } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { PageHead, Loading, ErrorState, Pill, DataTable } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import Filters from './Filters';
import DateRange, { toISO, addDays, startOfDay } from './DateRange';
import s from './Revenue.module.css';

const n = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));
const inr = (v, dp = 2) => (v == null ? '—'
  : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);
const pctOf = (v) => (v == null ? '—' : `${v}%`);
// Every label on this screen, explained where it is read.
//
// The device ledger already learned this: a number on a console that cannot be
// interrogated gets either believed blindly or ignored, and both are worse than
// a number with its definition attached. Several of these say what the figure
// used to be and why it was wrong — that history is the most useful thing a
// reader can know about a number that has already been corrected once.
const EXPLAIN = {
  advertiserRevenue:
    'Accrual under campaigns that are ACTIVE right now. This is the hero because '
    + 'the total is dominated by paused seed campaigns owned by the fleet operator: '
    + 'folding those into one headline is arithmetically right and commercially a lie.',
  pausedRevenue:
    'Accrual under campaigns whose status is currently paused. It is shown, not '
    + 'hidden, because hiding it would make the total impossible to reconcile against '
    + 'billing — and because a paused campaign still accruing is worth seeing.',
  totalAccrued:
    'Display money under the presence regime: blanket accrual before 1 Aug 2026, '
    + 'presence-verified money from it (until V353 lands the mart, this degrades to '
    + 'the blanket figure and the regime line says so). Reconciliation below stays on '
    + 'the BLANKET accrual deliberately — books are never filtered — so across the '
    + 'epoch this headline and the recon line legitimately differ.',
  regime:
    'Which pricing regime the display money on this screen carries. Before 1 Aug 2026 '
    + 'a day shows its blanket accrual, as billed then; from 1 Aug 2026 only presence-'
    + 'verified money counts. Reconciliation is books and never wears this filter.',

  reconAccrued:
    'Summed from impression_batches.total_amount_cents (PAISE, converted here). This '
    + 'is what delivery says was earned.',
  reconCharged:
    'Summed from billing_ledger ad_spend and its corrections (already RUPEES — the two '
    + 'money tables use different units, and applying one divisor to both is a 100x '
    + 'error). This is what was actually charged to accounts.',
  reconDelta:
    'Charged minus accrued. Healthy looks like small deltas that ALTERNATE SIGN and '
    + 'cancel out — the ledger rolls batches up on its own schedule, so a batch '
    + 'delivered late on one day is charged on the next. A delta that drifts one way '
    + 'and keeps growing is the thing to act on.',
  freshness:
    'Revenue is served from a materialised table refreshed hourly, and the refresh '
    + 'refuses to report success unless every day it wrote matches the live derivation '
    + 'EXACTLY. Reconciliation above is computed from the source every time, so it '
    + 'cannot go stale — which makes a growing delta an independent staleness check.',

  cpmBillable:
    'Rupees per thousand ADVERTISER-FACING impressions — total accrued over total '
    + 'billable. Recomputed from the window\u2019s sums, never averaged from daily CPMs, '
    + 'which would weight a 40-play day like a 30,000-play one.',
  cpmAll:
    'The same mille over EVERY play, charged or not. The gap between the two CPMs is '
    + 'what the presence gate withholds. Across the 28 Aug cutover this one falls '
    + 'sharply while the advertiser-facing CPM holds \u2014 that is the gate working, '
    + 'not revenue collapsing.',
  plays: 'Every creative play in the window, summed from impression_count.',
  billable:
    'Plays counting towards an advertiser under billing-service\u2019s own gate, mirrored '
    + 'term for term: presence verified, OR ended before the 28 Aug cutover, OR already '
    + 'processed with money attached. NOT one of the five verdict buckets below \u2014 it '
    + 'OVERLAPS them, which is why it is shown up here instead.',
  emptyRate:
    'Share of VERDICTED plays with nobody aboard \u2014 empty over (empty + aboard). It '
    + 'used to divide by advertiser-facing plays, which folded in pre-cutover plays that '
    + 'were never measured and read 2.5% where the truth was 64%.',
  coverage:
    'Share of plays carrying a presence verdict at all. Low over a long window because '
    + 'nothing before the 28 Aug cutover was ever verdicted; low on today because the '
    + 'sweep runs about two hours behind.',
  accruedGated:
    'Money on batches the gate would charge for, against total accrued. Equal whenever '
    + 'billing prices only what it has already gated \u2014 a gap here means this screen '
    + 'has drifted from the invoices.',

  colAdvertiser: 'The tenant that owns the campaign, joined live \u2014 never frozen, so a '
    + 'renamed or re-attributed advertiser corrects across all history.',
  colCampaigns: 'Distinct campaigns contributing in this window.',
  colCabs: 'Distinct devices that played this campaign. Counted per day and NOT summed '
    + 'across days \u2014 the same cab on two days is one cab, so this is the largest '
    + 'single-day figure, not a total.',
  colStatus: 'The campaign\u2019s status RIGHT NOW, joined live. Deliberately not stored '
    + 'with the numbers: freezing it would split one campaign across two statuses inside '
    + 'a single answer.',
  colAccrued: 'Rupees attached to this row\u2019s batches in the window.',
  colPlays: 'Creative plays, summed from impression_count \u2014 not a count of batches.',
  colCpm: 'Accrued over advertiser-facing impressions \u00d7 1000, recomputed from this '
    + 'row\u2019s own sums.',
  colDay: 'IST calendar day \u2014 the same bucket billing, Brands Manager and the signals '
    + 'plane all use.',
  colReconDelta: 'Charged minus accrued for that day. Alternating signs are roll-up lag; '
    + 'a run of same-signed deltas is not.',
};

const fmtAge = (sec) => {
  if (sec == null) return 'an unknown time';
  if (sec < 90) return `${Math.round(sec)}s`;
  if (sec < 5400) return `${Math.round(sec / 60)} min`;
  if (sec < 172800) return `${Math.round(sec / 3600)} h`;
  return `${Math.round(sec / 86400)} d`;
};

export default function Revenue() {

  // Filter state lives in the URL: a filtered view of company revenue is
  // exactly the thing someone pastes into a message, and query params make
  // that shareable and refresh-proof for free.
  const [params, setParams] = useSearchParams();
  const readList = useCallback(
    (k) => (params.get(k) ? params.get(k).split(',').filter(Boolean) : []), [params]);
  const filters = useMemo(() => ({
    advertisers: readList('advertisers'),
    statuses: readList('statuses'),
    campaigns: readList('campaigns'),
  }), [readList]);

  const setFilters = (next) => {
    const p = new URLSearchParams(params);
    for (const k of ['advertisers', 'statuses', 'campaigns']) {
      if (next[k]?.length) p.set(k, next[k].join(',')); else p.delete(k);
    }
    setParams(p, { replace: true });
  };

  // The window is a real date range, in the URL like everything else. Default
  // is the last 30 days, expressed as explicit dates rather than a magic
  // number, so the URL always states the window it is showing.
  const today = useMemo(() => startOfDay(new Date()), []);
  const range = useMemo(() => ({
    from: params.get('from') || toISO(addDays(today, -29)),
    to: params.get('to') || toISO(today),
  }), [params, today]);

  const setRange = (from, to) => {
    const p = new URLSearchParams(params);
    p.set('from', from); p.set('to', to);
    setParams(p, { replace: true });
  };

  const qs = useMemo(() => {
    const p = new URLSearchParams({ from: range.from, to: range.to });
    for (const k of ['advertisers', 'statuses', 'campaigns']) {
      if (filters[k].length) p.set(k, filters[k].join(','));
    }
    return p.toString();
  }, [range, filters]);

  const { data, error, loading, reload } = useApi(
    () => api.get(`/insights/revenue?${qs}`).then((r) => r.data?.data), [qs]);

  if (loading) return <div className={s.wrap}><Loading what="company revenue" /></div>;
  if (error) return <div className={s.wrap}><ErrorState error={error} onRetry={reload} /></div>;
  if (!data) return null;

  const { totals, verdicts, byStatus, byAdvertiser, byCampaign, daily, recon, source,
          filters: fInfo, facets } = data;
  const active = byStatus.find((b) => b.status === 'active');
  const paused = byStatus.find((b) => b.status === 'paused');

  // The recon delta is only meaningful with its direction. Small and alternating
  // is roll-up lag; one-directional drift is the thing to act on.
  const reconTone = Math.abs(recon.delta_pct ?? 0) < 1 ? 'good' : 'warn';

  return (
    <div className={s.wrap}>
      <PageHead
        title="Company revenue"
        sub="What the business earned, across every advertiser — accrual from the signals plane, reconciled against the billing ledger"
        actions={<DateRange from={range.from} to={range.to} onChange={setRange} />}
      />

      {facets && (
        <Filters
          facets={facets}
          value={filters}
          onChange={setFilters}
          summary={fInfo?.active ? {
            active: true,
            share: fInfo.total_accrued_inr > 0
              ? `${Math.round((totals.accrued_inr / fInfo.total_accrued_inr) * 1000) / 10}%`
              : '—',
            totalLabel: inr(fInfo.total_accrued_inr, 0),
          } : null}
        />
      )}

      {/* THE HERO. Active-advertiser revenue is the business; the total is the
          number that must tie out to billing. Both, never one. */}
      {fInfo?.active && fInfo.matched_rows === 0 && (
        <div className={s.warnBanner}>
          No revenue matches these filters in {range.from} – {range.to}. The numbers below are all zero
          because nothing matched — not because nothing happened.
        </div>
      )}

      <div className={s.hero}>
        <div className={`${s.heroCard} ${s.heroPrimary}`}>
          <span className={s.heroLabel} title={EXPLAIN.advertiserRevenue}>Advertiser revenue</span>
          <span className={s.heroValue}>{inr(active?.accrued_inr, 0)}</span>
          <span className={s.heroSub}>
            {active ? `${active.campaigns} active campaigns · ${n(active.plays)} plays` : 'no active campaigns'}
          </span>
        </div>
        <div className={s.heroCard}>
          <span className={s.heroLabel} title={EXPLAIN.pausedRevenue}>Paused campaigns</span>
          <span className={`${s.heroValue} ${s.heroMuted}`}>{inr(paused?.accrued_inr, 0)}</span>
          <span className={s.heroSub}>
            {paused ? `${paused.campaigns} campaigns still accruing · ${n(paused.plays)} plays` : '—'}
          </span>
        </div>
        <div className={s.heroCard}>
          <span className={s.heroLabel} title={EXPLAIN.totalAccrued}>Total accrued</span>
          <span className={s.heroValue}>{inr(totals.accrued_inr, 0)}</span>
          <span className={s.heroSub}>
            {data.regime?.degraded
              ? data.regime.degraded
              : (data.regime?.note || 'the figure that must tie to billing')}
          </span>
        </div>
      </div>

      {/* WHEN WAS THIS TRUE? A materialised number that does not say how old it
          is asks to be trusted on faith. The mart refreshes hourly; the API
          calls it stale past 6h (four missed runs), and that is a fault worth
          interrupting the page for rather than a footnote. */}
      {source?.stale ? (
        <div className={s.staleBanner}>
          <strong>These numbers are stale.</strong> The revenue mart last refreshed{' '}
          {source.refreshed_at ? new Date(source.refreshed_at).toLocaleString() : 'never'}
          {source.age_seconds != null && ` — ${fmtAge(source.age_seconds)} ago`}. It should
          refresh hourly. Reconciliation below still reads the source directly, so a growing
          delta there confirms it.
        </div>
      ) : null}

      {/* RECONCILIATION, on the face of the screen rather than in an audit
          somewhere. A revenue number nobody has checked against the ledger is
          just a query result. */}
      <div className={s.recon}>
        <span className={s.reconTitle}>Reconciliation</span>
        {recon.applies_to_filter === false && (
          <span className={s.unfilteredTag}>unfiltered</span>
        )}
        <span className={s.reconItem} title={EXPLAIN.reconAccrued}>accrued <b>{inr(recon.accrued_inr)}</b></span>
        <span className={s.reconArrow}>vs</span>
        <span className={s.reconItem} title={EXPLAIN.reconCharged}>charged <b>{inr(recon.charged_inr)}</b></span>
        <span title={EXPLAIN.reconDelta}><Pill tone={reconTone}>
          {recon.delta_inr >= 0 ? '+' : ''}{inr(recon.delta_inr)} ({recon.delta_pct}%)
        </Pill></span>
        <span className={s.reconNote}>
          {recon.applies_to_filter === false
            ? 'NOT filtered — reconciliation is at billing-account grain, because a ledger entry '
              + 'aggregates many batches and names only one. It cannot follow a campaign or status '
              + 'filter, so this compares the whole account against whole-account accrual.'
            : 'accrual is per batch (paise); charge is the billing ledger (rupees), rolled up on its '
              + 'own schedule — small alternating deltas are lag, one-directional drift is not'}
        </span>
        <span className={s.freshness} title={EXPLAIN.freshness}>
          {source?.kind === 'mart' ? 'mart' : 'live'} · refreshed {fmtAge(source?.age_seconds)} ago
        </span>
      </div>

      {/* Both CPMs, never one. Across the presence-gate cutover they diverge
          hard, and a single "CPM" reads a working gate as a revenue collapse. */}
      <div className={s.strip}>
        {data.regime && (
          <Metric label="Money regime"
                  value={data.regime.degraded ? 'blanket · V353 pending' : data.regime.note}
                  hint={data.regime.degraded || EXPLAIN.regime} />
        )}
        <Metric label="CPM · advertiser-facing" value={inr(totals.cpm_billable_inr)}
                hint={EXPLAIN.cpmBillable} />
        <Metric label="CPM · all plays" value={inr(totals.cpm_all_plays_inr)}
                hint={EXPLAIN.cpmAll} />
        <Metric label="Plays" value={n(totals.plays)}
                hint={EXPLAIN.plays} />
        <Metric label="Advertiser-facing" value={n(totals.billable)}
                hint={EXPLAIN.billable} />
        <Metric label="Empty rate" value={pctOf(totals.empty_rate_pct)} tone="warn"
                hint={EXPLAIN.emptyRate} />
        {totals.accrued_gated_inr !== totals.accrued_inr && (
          <Metric label="Accrued · under gate" value={inr(totals.accrued_gated_inr)} tone="warn"
                  hint={EXPLAIN.accruedGated} />
        )}
        <Metric label="Verdict coverage" value={pctOf(totals.verdict_coverage_pct)}
                hint={EXPLAIN.coverage} />
      </div>

      {/* The partition, shown as a partition. If this ever stops summing to
          plays the server says so and we surface it rather than rendering a
          breakdown that silently does not add up. */}
      {!verdicts.partitions && (
        <div className={s.warnBanner}>
          Verdict buckets do not sum to plays — the breakdown below is not a partition. Do not trust the split.
        </div>
      )}

      {/* THE PARTITION, shown. occupied + empty + vision_out + awaiting +
          pre_gate accounts for every play; `billable` is deliberately NOT here
          because it is a gate OUTCOME that overlaps occupied and pre_gate, and
          rendering it alongside these would appear to double-count. */}
      <Panel title="Where every play went"
             sub="these five account for every play; advertiser-facing overlaps them and is shown above, not here">
        <div className={s.buckets}>
          <Bucket label="Passengers aboard" value={verdicts.occupied} total={totals.plays} tone="good"
                  hint="Presence verified — a passenger was in the cab." />
          <Bucket label="Empty cab" value={verdicts.empty} total={totals.plays} tone="warn"
                  hint="Verdicted with nobody aboard. Played, not charged." />
          <Bucket label="Vision unavailable" value={verdicts.vision_out} total={totals.plays}
                  hint="The camera could not answer. Not charged." />
          <Bucket label="Awaiting verdict" value={verdicts.awaiting} total={totals.plays}
                  hint="Verdict sweep has not reached these yet — it runs about two hours behind." />
          <Bucket label="Before the gate" value={verdicts.pre_gate} total={totals.plays}
                  hint="Played before the presence gate went live on 28 Aug, so never measured. Billable by the pre-cutover term." />
        </div>
      </Panel>

      <div className={s.panels}>
        <Panel title="By advertiser" sub="active and paused combined; sorted by accrual">
          <DataTable
            columns={[
              { key: 'advertiser', label: 'Advertiser', title: EXPLAIN.colAdvertiser },
              { key: 'campaigns', label: 'Campaigns', align: 'num', title: EXPLAIN.colCampaigns, render: (r) => n(r.campaigns) },
              { key: 'plays', label: 'Plays', align: 'num', title: EXPLAIN.colPlays, render: (r) => n(r.plays) },
              { key: 'accrued_inr', label: 'Accrued', align: 'num', title: EXPLAIN.colAccrued, render: (r) => inr(r.accrued_inr) },
              { key: 'cpm_billable_inr', label: 'CPM', align: 'num', title: EXPLAIN.colCpm, render: (r) => inr(r.cpm_billable_inr) },
            ]}
            rows={byAdvertiser}
            getKey={(r) => r.key}
            initialSort={{ key: 'accrued_inr', dir: 'desc' }}
          />
        </Panel>

        <Panel title="By campaign" sub="status shown, because a paused campaign still accruing is the thing worth seeing">
          <DataTable
            columns={[
              { key: 'campaign_name', label: 'Campaign', title: 'Campaign name, joined live from brand_campaigns.' },
              { key: 'campaign_status', label: 'Status', title: EXPLAIN.colStatus,
                render: (r) => <Pill tone={r.campaign_status === 'active' ? 'good' : 'pend'}>{r.campaign_status}</Pill> },
              { key: 'advertiser', label: 'Advertiser', title: EXPLAIN.colAdvertiser },
              { key: 'devices', label: 'Cabs', align: 'num', title: EXPLAIN.colCabs, render: (r) => n(r.devices) },
              { key: 'plays', label: 'Plays', align: 'num', title: EXPLAIN.colPlays, render: (r) => n(r.plays) },
              { key: 'accrued_inr', label: 'Accrued', align: 'num', title: EXPLAIN.colAccrued, render: (r) => inr(r.accrued_inr) },
              { key: 'cpm_billable_inr', label: 'CPM', align: 'num', title: EXPLAIN.colCpm, render: (r) => inr(r.cpm_billable_inr) },
            ]}
            rows={byCampaign}
            getKey={(r) => r.key}
            initialSort={{ key: 'accrued_inr', dir: 'desc' }}
            filterPlaceholder="filter campaigns"
            filterOn={['campaign_name', 'advertiser', 'campaign_status']}
          />
        </Panel>
      </div>

      <Panel title="Daily" sub="both CPMs, so the presence gate reads as a gate and not as a revenue collapse">
        <div className={s.scroll}>
          <table className={s.table}>
            <thead>
              <tr>
                <th title={EXPLAIN.colDay}>Day</th>
                <th className={s.num} title={EXPLAIN.colPlays}>Plays</th>
                <th className={s.num} title={EXPLAIN.billable}>Advertiser-facing</th>
                <th className={s.num} title={EXPLAIN.colAccrued}>Accrued</th>
                <th className={s.num} title={EXPLAIN.cpmBillable}>CPM · facing</th>
                <th className={s.num} title={EXPLAIN.cpmAll}>CPM · all</th>
                <th className={s.num} title={EXPLAIN.emptyRate}>Empty</th>
                <th className={s.num} title={EXPLAIN.coverage}>Coverage</th>
                <th className={s.num} title={EXPLAIN.colReconDelta}>Recon Δ</th>
              </tr>
            </thead>
            <tbody>
              {daily.map((d) => {
                const rec = recon.rows.find((r) => r.day === d.day);
                return (
                  <tr key={d.day}>
                    <th scope="row" className={s.day}>{d.day}</th>
                    <td className={s.num}>{n(d.plays)}</td>
                    <td className={s.num}>{n(d.billable)}</td>
                    <td className={s.num}>{inr(d.accrued_inr)}</td>
                    <td className={s.num}>{inr(d.cpm_billable_inr)}</td>
                    <td className={`${s.num} ${s.dim}`}>{inr(d.cpm_all_plays_inr)}</td>
                    <td className={s.num}>{pctOf(d.empty_rate_pct)}</td>
                    <td className={s.num}>{pctOf(d.verdict_coverage_pct)}</td>
                    <td className={`${s.num} ${rec && Math.abs(rec.delta_inr) > 500 ? s.warn : s.dim}`}>
                      {rec ? inr(rec.delta_inr) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      <p className={s.foot}>
        Accrual from <code>signals.company_day_revenue</code> (V315), reconciled against{' '}
        <code>signals.v_company_day_recon</code>. The billable gate mirrors billing-service&rsquo;s
        own predicate term-for-term; per-advertiser spend is owned by billing-service, not by this
        screen. Verified on a closed window to equal the sum of every{' '}
        <NavLink to="/v2/fleet" className={s.link}>device ledger</NavLink> to the paise.
      </p>
    </div>
  );
}

function Metric({ label, value, hint, tone }) {
  return (
    <div className={s.metric} title={hint}>
      <span className={s.metricLabel}>{label}</span>
      <span className={`${s.metricValue} ${tone ? s[tone] : ''}`}>{value}</span>
    </div>
  );
}

// A bucket shows its COUNT and its share of plays. The share is what makes the
// partition legible — five raw numbers do not add up to anything a reader can
// check at a glance, five percentages do.
function Bucket({ label, value, total, tone, hint }) {
  const pct = total > 0 ? Math.round((value / total) * 1000) / 10 : null;
  return (
    <div className={s.bucket} title={hint}>
      <span className={s.bucketLabel}>{label}</span>
      <span className={`${s.bucketValue} ${tone ? s[tone] : ''}`}>{n(value)}</span>
      <span className={s.bucketPct}>{pct == null ? '—' : `${pct}%`}</span>
    </div>
  );
}

function Panel({ title, sub, children }) {
  return (
    <section className={s.panel}>
      <div className={s.panelHead}>
        <span className={s.panelTitle}>{title}</span>
        {sub && <span className={s.panelSub}>{sub}</span>}
      </div>
      {children}
    </section>
  );
}
